import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "../../utils/framer-motion-custom";
import Heading2 from "../Typography/Heading2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";

const Accordion = ({
  i,
  expanded,
  setExpanded,
  header,
  content,
  containerClassNames,
  headerSize,
  headerColor,
  justifyIcon,
  hoverBorder
}) => {
  const isOpen = i === expanded;
  const headerRowClasses = classNames(
    "flex",
    "items-center",
    "pointer",
    "relative",
    { "justify-start": justifyIcon == "left" },
    { "justify-between": justifyIcon == "right" },
    { "outline-0": isOpen }
  );
  const headerClasses = classNames("pr3", {
    "flex-auto": justifyIcon == "right"
  });

  const iconWeight = headerSize == "large" ? "far" : "fal";

  const toggle = () => {
    // Toggle the accordion if the element on which the event was triggered is the accordion.
    if (motionDiv.current === document.activeElement) {
      setExpanded(isOpen ? false : i);
    }
  };

  const motionDiv = useRef(null);
  const containerDiv = useRef(null);

  const enterPress = e => {
    if (e.keyCode === 13 && motionDiv.current === document.activeElement) {
      e.preventDefault();
      toggle();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", enterPress);
    return () => {
      document.removeEventListener("keydown", enterPress);
    };
  }, []);

  /* This spacing math is necessary because when the accordion is closed
  we want the entire tab to toggle open & close but having padding or
  margin on the content part of the tab makes the animation jumpy. So this
  will take whatever padding is on the whole container adds it to the header
  row and then applies a negative margin with the same value to cancel out
  the spacing but leave it all clickable */
  useEffect(() => {
    const padding = window
      .getComputedStyle(containerDiv.current, null)
      .getPropertyValue("padding");
    const paddingArray = padding.split(" ");
    let negativeMargin = [];
    for (let i = 0; i < paddingArray.length; ++i) {
      negativeMargin.push("-" + paddingArray[i]);
    }
    motionDiv.current.style.padding = padding;
    motionDiv.current.style.margin = negativeMargin.join(" ");
  }, []);

  const onMouseEnter = () => {
    if (hoverBorder) {
      containerDiv.current.classList.add("bg-gray-100");
    }
  };

  const onMouseLeave = () => {
    if (hoverBorder) {
      containerDiv.current.classList.remove("bg-gray-100");
    }
  };

  useEffect(() => {
    if (hoverBorder && i > 0) {
      // Hack for collapsing borders
      containerDiv.current.style.top = "-1px";
    }
  });

  return (
    <div ref={containerDiv} className={containerClassNames}>
      <motion.div
        initial={false}
        onClick={toggle}
        className={headerRowClasses}
        tabIndex={0}
        ref={motionDiv}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className={headerClasses}>
          {headerSize == "large" && (
            <Heading2 inline={true} color={headerColor}>
              {header}
            </Heading2>
          )}
          {headerSize == "small" && <>{header}</>}
        </div>
        <div>
          <FontAwesomeIcon
            icon={[iconWeight, "chevron-right"]}
            className={headerColor}
            transform={isOpen ? { rotate: 90 } : { rotate: 0 }}
          />
        </div>
      </motion.div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 }
            }}
            transition={{
              ease: [0.04, 0.62, 0.23, 0.98],
              duration: 0
            }}
          >
            <div className="pt2">{content}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

Accordion.defaultProps = {
  headerSize: "large",
  justifyIcon: "right",
  hoverBorder: false,
  headerColor: "black-80"
};

Accordion.propTypes = {
  i: PropTypes.number,
  expanded: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  setExpanded: PropTypes.func,
  header: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  containerClassNames: PropTypes.string,
  headerSize: PropTypes.oneOf(["small", "large"]),
  headerColor: PropTypes.string,
  justifyIcon: PropTypes.oneOf(["right", "left"]),
  hoverBorder: PropTypes.bool
};

export default Accordion;
